import { makeCustomEventBindings } from './make-custom-event-bindings.js'

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const emitLocalStorageChange = makeCustomEventBindings<{ key: string; value?: any }>('typed-local-storage-change')

export class TypedLocalStorage<Schema extends object> {
  onChange<K extends keyof Schema>(
    key: K,
    callback: (value: Schema[K] | undefined) => void,
  ): () => void {
    return emitLocalStorageChange.listen(({ key: changedKey, value }) => {
      if (String(key) === changedKey) {
        callback(value as Schema[K] | undefined)
      }
    })
  }

  getItem<K extends keyof Schema>(key: K, defaultValue?: Schema[K]): Schema[K] | undefined {
    const value = localStorage.getItem(String(key))
    if (value === null) return defaultValue
    try {
      return JSON.parse(value) as Schema[K]
    } catch (e) {
      // If parsing fails, it probably means that a raw string was stored.
      // If the default value is a string, we'll assume that the stored
      // value should be returned as-is. If default value is not provided let's return string
      if (typeof defaultValue === 'string' || typeof value === 'string')
        return value as unknown as Schema[K]
      // Otherwise, transform the error into something more meaningful if needed.
      throw e
    }
  }

  setItem<K extends keyof Schema>(key: K, value: Schema[K]): void {
    localStorage.setItem(String(key), JSON.stringify(value))
    emitLocalStorageChange({ key: String(key), value })
  }

  setItemUnsafe<K extends keyof Schema>(key: K, value: string): void {
    localStorage.setItem(String(key), value)
    emitLocalStorageChange({ key: String(key), value })
  }

  removeItem<K extends keyof Schema>(key: K): void {
    localStorage.removeItem(String(key))
    emitLocalStorageChange({ key: String(key) })
  }
}

export class TypedInMemoryStorage<Schema extends object> {
  private store: Map<string, string> = new Map()

  onChange<K extends keyof Schema>(
    key: K,
    callback: (value: Schema[K] | undefined) => void,
  ): () => void {
    return emitLocalStorageChange.listen(({ key: changedKey, value }) => {
      if (String(key) === changedKey) {
        callback(value as Schema[K] | undefined)
      }
    })
  }

  getItem<K extends keyof Schema>(key: K, defaultValue?: Schema[K]): Schema[K] | undefined {
    const raw = this.store.get(String(key))
    if (raw === undefined) return defaultValue

    try {
      return JSON.parse(raw) as Schema[K]
    } catch (e) {
      // If parsing fails, assume it's a plain string
      if (typeof defaultValue === 'string' || typeof raw === 'string') {
        return raw as unknown as Schema[K]
      }
      throw e
    }
  }

  setItem<K extends keyof Schema>(key: K, value: Schema[K]): void {
    this.store.set(String(key), JSON.stringify(value))
    emitLocalStorageChange({ key: String(key), value })
  }

  setItemUnsafe<K extends keyof Schema>(key: K, value: string): void {
    this.store.set(String(key), value)
    emitLocalStorageChange({ key: String(key), value })
  }

  removeItem<K extends keyof Schema>(key: K): void {
    this.store.delete(String(key))
    emitLocalStorageChange({ key: String(key) })
  }

  clear(): void {
    this.store.clear()
  }
}

export function makeTypedInMemoryStorage<Schema extends object>(
  initialState: Schema,
): TypedInMemoryStorage<Schema> {
  const storage = new TypedInMemoryStorage<Schema>()

  const kvs = Object.entries(initialState) as [keyof Schema, Schema[keyof Schema]][]
  kvs.forEach(([key, value]) => storage.setItem(key, value))

  return storage
}
